import {
  type GeneralChecksResponse,
  STARTER_GENERAL_CHECKS,
  generalChecksResponseSchema,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useEffect, useRef, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPost, apiPut } from '../api/client';
import {
  type CheckRow,
  addCheckRow,
  checkRowsFrom,
  checksDirty,
  moveCheckRow,
  toChecksRequest,
  validateCheckRows,
} from './general-checks-draft';
import { confirmDiscardPending, reportPendingChanges, warnOnUnload } from './unsaved-changes';
import { useLoad } from './use-master-data';

// "בדיקות ומסירה כללית" in "הגדרות מסירות" (H-Next): the general handover checks (keys, remotes,
// parking gate, parking and storage, spare tiles, ...), part of the inspection template. A check may
// count a quantity (with its unit).

type SaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved' }
  | { readonly kind: 'conflict' }
  | { readonly kind: 'error'; readonly text: string };

function ChecksBody({
  projectId,
  list,
  justSaved,
  onSaved,
  onReload,
}: {
  readonly projectId: string;
  readonly list: GeneralChecksResponse;
  readonly justSaved: boolean;
  readonly onSaved: (list: GeneralChecksResponse) => void;
  readonly onReload: () => void;
}) {
  const [rows, setRows] = useState<CheckRow[]>(() => checkRowsFrom(list));
  const [save, setSave] = useState<SaveState>({ kind: justSaved ? 'saved' : 'idle' });
  const [showErrors, setShowErrors] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const focusKey = useRef<string | null>(null);
  const editable = list.permissions.edit;
  const dirty = checksDirty(rows, list);
  const errors = showErrors ? validateCheckRows(rows) : {};

  useEffect(() => {
    reportPendingChanges(dirty ? 1 : 0);
    window.addEventListener('beforeunload', warnOnUnload);
    return () => {
      window.removeEventListener('beforeunload', warnOnUnload);
      reportPendingChanges(0);
    };
  }, [dirty]);
  useEffect(() => {
    if (focusKey.current === null) return;
    document.getElementById(`check-name-${focusKey.current}`)?.focus();
    focusKey.current = null;
  });

  const change = (next: (r: CheckRow[]) => CheckRow[]) => {
    setRows(next);
    if (save.kind !== 'saving') setSave({ kind: 'idle' });
  };
  const update = (key: string, field: 'name' | 'unit' | 'active', value: string | boolean) => {
    change((all) => all.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const submit = async () => {
    if (Object.keys(validateCheckRows(rows)).length > 0) {
      setShowErrors(true);
      setSave({ kind: 'error', text: 'יש בדיקות שדורשות תיקון. הרשימה לא נשמרה.' });
      return;
    }
    setSave({ kind: 'saving' });
    try {
      const saved = await apiPut(
        `/projects/${projectId}/general-checks`,
        toChecksRequest(rows, list.listVersion),
        generalChecksResponseSchema,
      );
      setShowErrors(false);
      setSave({ kind: 'saved' });
      onSaved(saved);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) setSave({ kind: 'conflict' });
      else if (error instanceof ApiError && error.status === 422)
        setSave({ kind: 'error', text: 'השרת דחה חלק מהבדיקות (למשל שם כפול). בדקו ונסו שוב.' });
      else if (error instanceof ApiError && error.status === 403)
        setSave({ kind: 'error', text: 'אין לך הרשאה לערוך את הבדיקות הכלליות.' });
      else if (error instanceof NetworkError)
        setSave({ kind: 'error', text: 'אין חיבור לשרת. השינויים עדיין כאן; נסו שוב.' });
      else setSave({ kind: 'error', text: 'השמירה נכשלה. השינויים עדיין כאן; נסו שוב.' });
    }
  };
  const starter = async () => {
    if (
      !(await askConfirm({
        title: 'ליצור בדיקות כלליות התחלתיות?',
        message: `ייווצרו ${String(STARTER_GENERAL_CHECKS.length)} בדיקות כלליות לפי אב־הטיפוס (מפתחות, שלטים, שער החניה, חניה, מחסן ואריחי בדק). אפשר לשנות הכול אחר כך.`,
        confirmLabel: 'יצירת הבדיקות',
      }))
    )
      return;
    setSave({ kind: 'saving' });
    try {
      const created = await apiPost(
        `/projects/${projectId}/general-checks/starter`,
        {},
        generalChecksResponseSchema,
      );
      setSave({ kind: 'saved' });
      onSaved(created);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) setSave({ kind: 'conflict' });
      else setSave({ kind: 'error', text: 'יצירת הבדיקות נכשלה. נסו שוב.' });
    }
  };

  return (
    <div className="template-editor general-checks-editor">
      <p className="hint">
        בדיקות המסירה הכללית בכל דירה, בסדר שבו יוצגו. בבדיקה שסופרת כמות (למשל מפתחות) מציינים את
        יחידת הכמות. שינוי חל על מסירות שייפתחו מעכשיו; מסירות קיימות שומרות את הבדיקות שהתחילו בהן.
        גרסת תבנית: {list.revision}.
      </p>
      {rows.length === 0 && (
        <div className="notice">
          <p>אין עדיין בדיקות כלליות.</p>
          {editable && (
            <button
              type="button"
              className="button button--primary"
              disabled={save.kind === 'saving'}
              onClick={() => {
                void starter();
              }}
            >
              יצירת בדיקות התחלתיות (מאב־הטיפוס)
            </button>
          )}
        </div>
      )}
      {rows.length > 0 && (
        <ol className="catalog-list">
          {rows.map((row, index) => {
            const e = errors[row.key] ?? {};
            const name = row.name.trim() === '' ? 'בדיקה חדשה' : row.name;
            return (
              <li
                key={row.key}
                className={`catalog-row template-row${row.active ? '' : ' catalog-row--inactive'}`}
                aria-label={name}
              >
                <label className="field catalog-row__name">
                  <span>בדיקה</span>
                  <input
                    id={`check-name-${row.key}`}
                    value={row.name}
                    disabled={!editable}
                    aria-invalid={e.name !== undefined}
                    onChange={(event) => {
                      update(row.key, 'name', event.target.value);
                    }}
                  />
                  {e.name !== undefined && <p className="field__error">{e.name}</p>}
                </label>
                <label className="field template-row__types">
                  <span>יחידת כמות (ריק: ללא כמות)</span>
                  <input
                    value={row.unit}
                    disabled={!editable}
                    aria-invalid={e.unit !== undefined}
                    onChange={(event) => {
                      update(row.key, 'unit', event.target.value);
                    }}
                  />
                  {e.unit !== undefined && <p className="field__error">{e.unit}</p>}
                </label>
                <label className="catalog-row__active" title="סטטוס">
                  <input
                    type="checkbox"
                    checked={row.active}
                    disabled={!editable}
                    onChange={(event) => {
                      update(row.key, 'active', event.target.checked);
                    }}
                  />
                  <span>פעיל</span>
                </label>
                {row.id === null && <span className="chip chip--new">חדש</span>}
                {editable && (
                  <span className="catalog-row__move">
                    <button
                      type="button"
                      className="button button--small"
                      aria-label={`העברה למעלה: ${name}`}
                      disabled={index === 0}
                      onClick={() => {
                        change((all) => moveCheckRow(all, row.key, -1));
                      }}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="button button--small"
                      aria-label={`העברה למטה: ${name}`}
                      disabled={index === rows.length - 1}
                      onClick={() => {
                        change((all) => moveCheckRow(all, row.key, 1));
                      }}
                    >
                      ▼
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {editable && (
        <form
          className="template-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim() === '') return;
            const added = addCheckRow(rows, newName.trim(), newUnit.trim());
            change(() => added.rows);
            setNewName('');
            setNewUnit('');
            focusKey.current = added.key;
          }}
        >
          <label className="field">
            <span>בדיקה חדשה</span>
            <input
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
              }}
            />
          </label>
          <label className="field">
            <span>יחידת כמות (רשות)</span>
            <input
              value={newUnit}
              placeholder="יח׳"
              onChange={(e) => {
                setNewUnit(e.target.value);
              }}
            />
          </label>
          <button type="submit" className="button" disabled={newName.trim() === ''}>
            + הוספת בדיקה
          </button>
        </form>
      )}

      {editable && (
        <div className="save-bar" role="region" aria-label="שמירת הבדיקות הכלליות">
          {save.kind === 'conflict' ? (
            <div className="notice notice--error save-bar__notice" role="alert">
              <p>הבדיקות עודכנו במקום אחר מאז שנפתחו. השינויים שלך לא נשמרו.</p>
              <button
                type="button"
                className="button button--small"
                onClick={() => {
                  void confirmDiscardPending().then((ok) => {
                    if (ok) onReload();
                  });
                }}
              >
                טעינת הגרסה העדכנית
              </button>
            </div>
          ) : save.kind === 'error' ? (
            <p className="notice notice--error save-bar__notice" role="alert">
              {save.text}
            </p>
          ) : null}
          <p className={`save-bar__status${dirty ? ' save-bar__status--dirty' : ''}`} role="status">
            {save.kind === 'saving'
              ? 'שומר…'
              : dirty
                ? 'יש שינויים שלא נשמרו'
                : save.kind === 'saved'
                  ? 'הבדיקות נשמרו'
                  : 'כל השינויים שמורים'}
          </p>
          <div className="save-bar__actions">
            <button
              type="button"
              className="button"
              disabled={!dirty || save.kind === 'saving'}
              onClick={() => {
                void askConfirm({
                  title: 'לבטל את השינויים שלא נשמרו?',
                  confirmLabel: 'ביטול השינויים',
                  danger: true,
                }).then((yes) => {
                  if (!yes) return;
                  setRows(checkRowsFrom(list));
                  setShowErrors(false);
                  setSave({ kind: 'idle' });
                });
              }}
            >
              ביטול שינויים
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={!dirty || save.kind === 'saving'}
              onClick={() => {
                void submit();
              }}
            >
              שמירת הבדיקות
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function GeneralChecksEditor({ projectId }: { readonly projectId: string }) {
  const [version, setVersion] = useState(0);
  const [saved, setSaved] = useState<GeneralChecksResponse | null>(null);
  const [data, reload] = useLoad(`general-checks:${projectId}:${String(version)}`, () =>
    apiGet(`/projects/${projectId}/general-checks`, generalChecksResponseSchema),
  );
  const list = saved ?? (data.state === 'ready' ? data.data : null);
  if (list === null) {
    if (data.state === 'forbidden')
      return <p className="notice notice--error">אין לך הרשאה לצפות בבדיקות הכלליות.</p>;
    if (data.state === 'error')
      return (
        <div className="notice notice--error" role="alert">
          <p>טעינת הבדיקות הכלליות נכשלה.</p>
          <button type="button" className="button" onClick={reload}>
            נסו שוב
          </button>
        </div>
      );
    return (
      <div className="skeleton" role="status" aria-label="טוען נתונים">
        <div className="skeleton__row" />
        <div className="skeleton__row" />
      </div>
    );
  }
  return (
    <ChecksBody
      key={list.listVersion}
      projectId={projectId}
      list={list}
      justSaved={saved !== null}
      onSaved={setSaved}
      onReload={() => {
        setSaved(null);
        setVersion((v) => v + 1);
      }}
    />
  );
}
